import { z } from '@harvestiq/zod';
import { CommodityId } from '../CommodityId';
import { RmaLrpTypeCode } from '../lrp/LrpTypeCodes';
import {
  RmaLgmCommodityCode,
  RmaLgmCommodityCodes,
  RmaLgmCommodityNames,
} from './LgmCommodities';
import { LgmDeductibles } from './LgmInsurance';
import { RmaCommodityTypeCodes } from '../rma/CommodityTypeCodes';

// Subset of RMA Type Codes for LGM derived from RMA ADM A00540
export const RmaLgmTypeCodes = {
  FARROW_TO_FINISH: '804',
  FINISHING: '805',
  SEW_PIG_FINISHING: '806',
  CALF_FINISHING: '807',
  YEARLING_FINISHING: '808',
  NO_TYPE_SPECIFIED: '997',
} as const;

export type RmaLgmTypeCode =
  (typeof RmaLgmTypeCodes)[keyof typeof RmaLgmTypeCodes];

export function isRmaLgmTypeCode(typeCode: string): typeCode is RmaLgmTypeCode {
  return Object.values(RmaLgmTypeCodes).includes(typeCode as RmaLgmTypeCode);
}

export function isRmaLgmCattleTypeCode(
  typeCode: string
): typeCode is Extract<RmaLgmTypeCode, '807' | '808'> {
  return (
    typeCode === RmaLgmTypeCodes.CALF_FINISHING ||
    typeCode === RmaLgmTypeCodes.YEARLING_FINISHING
  );
}

/**
 * NOTE: These names do not match the RMA type names from ADM A00540.
 */
export const HiqLgmTypeCodeNames = {
  FARROW_TO_FINISH: 'Farrow to Finish',
  FINISHING_OPERATION: 'Finishing Operation',
  SEW_OPERATION: 'SEW Operation',
  CALF_FINISHING: 'Calf Finishing',
  YEARLING_FINISHING: 'Yearling Finishing',
  NO_TYPE_SPECIFIED: 'No Type Specified',
} as const;

export type HiqLgmTypeCodeNames =
  (typeof HiqLgmTypeCodeNames)[keyof typeof HiqLgmTypeCodeNames];

/**
 * @deprecated we should really know the type code everywhere we know the name
 */
export const LgmTypeCodeByName = {
  [HiqLgmTypeCodeNames.FARROW_TO_FINISH]: RmaLgmTypeCodes.FARROW_TO_FINISH,
  [HiqLgmTypeCodeNames.FINISHING_OPERATION]: RmaLgmTypeCodes.FINISHING,
  [HiqLgmTypeCodeNames.SEW_OPERATION]: RmaLgmTypeCodes.SEW_PIG_FINISHING,
  [HiqLgmTypeCodeNames.CALF_FINISHING]: RmaLgmTypeCodes.CALF_FINISHING,
  [HiqLgmTypeCodeNames.YEARLING_FINISHING]: RmaLgmTypeCodes.YEARLING_FINISHING,
  [HiqLgmTypeCodeNames.NO_TYPE_SPECIFIED]: RmaLgmTypeCodes.NO_TYPE_SPECIFIED,
};

const LgmCommodityInfoEntrySchema = z.object({
  rmaTypeCode: z.nativeEnum(RmaLgmTypeCodes),
  rmaTypeName: z.string(),
  rmaCommodityCode: z.nativeEnum(RmaLgmCommodityCodes),
  rmaCommodityTypeCode: z.nativeEnum(RmaCommodityTypeCodes),
  hiqTypeCodeName: z.nativeEnum(HiqLgmTypeCodeNames),
  rmaCommodityName: z.nativeEnum(RmaLgmCommodityNames),
  commodityId: z.nativeEnum(CommodityId),
});
type LgmCommodityInfoEntry = z.infer<typeof LgmCommodityInfoEntrySchema>;

export const LgmCommodityInfo: Record<RmaLgmTypeCode, LgmCommodityInfoEntry> = {
  [RmaLgmTypeCodes.FARROW_TO_FINISH]: {
    rmaTypeCode: RmaLgmTypeCodes.FARROW_TO_FINISH,
    rmaTypeName: 'Farrow To Finish',
    rmaCommodityCode: RmaLgmCommodityCodes.SWINE,
    rmaCommodityTypeCode: RmaCommodityTypeCodes.FarrowToFinish,
    hiqTypeCodeName: HiqLgmTypeCodeNames.FARROW_TO_FINISH,
    rmaCommodityName: RmaLgmCommodityNames.SWINE,
    commodityId: CommodityId.LEAN_HOGS,
  },
  [RmaLgmTypeCodes.FINISHING]: {
    rmaTypeCode: RmaLgmTypeCodes.FINISHING,
    rmaTypeName: 'Finishing',
    rmaCommodityCode: RmaLgmCommodityCodes.SWINE,
    rmaCommodityTypeCode: RmaCommodityTypeCodes.Finishing,
    hiqTypeCodeName: HiqLgmTypeCodeNames.FINISHING_OPERATION,
    rmaCommodityName: RmaLgmCommodityNames.SWINE,
    commodityId: CommodityId.LEAN_HOGS,
  },
  [RmaLgmTypeCodes.SEW_PIG_FINISHING]: {
    rmaTypeCode: RmaLgmTypeCodes.SEW_PIG_FINISHING,
    rmaTypeName: 'SEW Pig Finishing',
    rmaCommodityCode: RmaLgmCommodityCodes.SWINE,
    rmaCommodityTypeCode: RmaCommodityTypeCodes.SewPigFinishing,
    hiqTypeCodeName: HiqLgmTypeCodeNames.SEW_OPERATION,
    rmaCommodityName: RmaLgmCommodityNames.SWINE,
    commodityId: CommodityId.LEAN_HOGS,
  },
  [RmaLgmTypeCodes.CALF_FINISHING]: {
    rmaTypeCode: RmaLgmTypeCodes.CALF_FINISHING,
    rmaTypeName: 'Calf Finishing',
    rmaCommodityCode: RmaLgmCommodityCodes.CATTLE,
    rmaCommodityTypeCode: RmaCommodityTypeCodes.CalfFinishing,
    hiqTypeCodeName: HiqLgmTypeCodeNames.CALF_FINISHING,
    rmaCommodityName: RmaLgmCommodityNames.CATTLE,
    commodityId: CommodityId.LIVE_CATTLE,
  },
  [RmaLgmTypeCodes.YEARLING_FINISHING]: {
    rmaTypeCode: RmaLgmTypeCodes.YEARLING_FINISHING,
    rmaTypeName: 'Yearling Finishing',
    rmaCommodityCode: RmaLgmCommodityCodes.CATTLE,
    rmaCommodityTypeCode: RmaCommodityTypeCodes.YearlingFinishing,
    hiqTypeCodeName: HiqLgmTypeCodeNames.YEARLING_FINISHING,
    rmaCommodityName: RmaLgmCommodityNames.CATTLE,
    commodityId: CommodityId.LIVE_CATTLE,
  },
  [RmaLgmTypeCodes.NO_TYPE_SPECIFIED]: {
    rmaTypeCode: RmaLgmTypeCodes.NO_TYPE_SPECIFIED,
    rmaTypeName: 'No Type Specified',
    rmaCommodityCode: RmaLgmCommodityCodes.DAIRY_CATTLE,
    rmaCommodityTypeCode: RmaCommodityTypeCodes.NoCommodityTypeSpecified,
    hiqTypeCodeName: HiqLgmTypeCodeNames.NO_TYPE_SPECIFIED,
    rmaCommodityName: RmaLgmCommodityNames.DAIRY_CATTLE,
    commodityId: CommodityId.MILK_3,
  },
};

export const getLgmTypeName = (typeCode: RmaLgmTypeCode) => {
  return LgmCommodityInfo[typeCode]?.rmaCommodityName;
};

export const getLgmDeductiblesByTypeCode = (typeCode: RmaLgmTypeCode) => {
  const commodityTypeName = getLgmTypeName(typeCode);
  return LgmDeductibles[commodityTypeName];
};

type LgmLivestockCommodityId =
  | typeof CommodityId.LIVE_CATTLE
  | typeof CommodityId.LEAN_HOGS;
export const LgmCommodityNamesByCommodityId: Record<
  LgmLivestockCommodityId,
  RmaLgmCommodityNames
> = {
  [CommodityId.LIVE_CATTLE]: RmaLgmCommodityNames.CATTLE,
  [CommodityId.LEAN_HOGS]: RmaLgmCommodityNames.SWINE,
};

export const typeCodeHasTargetWeights = (
  typeCode: RmaLgmTypeCode | RmaLrpTypeCode
): boolean => {
  return (
    typeCode === RmaLgmTypeCodes.CALF_FINISHING ||
    typeCode === RmaLgmTypeCodes.YEARLING_FINISHING
  );
};

export function getRmaLgmTypeCodeInfoForCommodity(
  commodityCode: RmaLgmCommodityCode
): LgmCommodityInfoEntry[] {
  return Object.values(LgmCommodityInfo).filter(
    (info) => info.rmaCommodityCode === commodityCode
  );
}

/**
 * Flattens the LgmCommodityInfo object into a single array of LgmCommodityInfoEntry objects
 */
export function getAllLgmCommodityInfo(): LgmCommodityInfoEntry[] {
  return Object.values(LgmCommodityInfo);
}
